import { ReadonlyURLSearchParams } from 'next/navigation'
import queryString from 'query-string'

import { QuinceRouter } from '@src/modules/navigation'
import { getProductOptionNamesFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import {
  AddOnProductOptionGroup,
  AddOnProductOptionGroupItem,
} from '@src/quince-site-common/src/domains/AddOnProductOptionGroup'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { getURLStringFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

export const findExactOrContainingHandleGroupItem = (
  groupItems: AddOnProductOptionGroup['groupItems'],
  currentPathName: string,
): AddOnProductOptionGroupItem | undefined => {
  const exactHandleMatchItem = groupItems.find(item => {
    const adjustedHandle = item.productHandle.startsWith('/') ? item.productHandle : `/${item.productHandle}`
    return adjustedHandle === currentPathName
  })
  if (exactHandleMatchItem) return exactHandleMatchItem

  return groupItems.find(item => (isBrowser() ? window.location.href.includes(item.productHandle) : false))
}

export const getFullNameFromHyphenatedName = (hypenatedName: string) =>
  hypenatedName.charAt(0).toUpperCase() + hypenatedName.slice(1).replace(/-([a-z])/g, g => ` ${g[1]?.toUpperCase()}`)

export const getOptionBasedOnQueryString = (
  product: Product | Bundle,
  optionKey: string,
  optionName: ProductOptionName,
  queryParamString?: string,
): ProductVariantOption | undefined => {
  const query = queryParamString ?? (isBrowser() ? window.location.search : '')
  let option: string | undefined = undefined
  if (query) {
    const queryParams = queryString.parse(query)
    // un-encoded marketing deep links (e.g. deep_link_value holding a nested product URL) duplicate
    // option params, making query-string return an array; take the first (page URL's own) value
    const rawOptionValue = queryParams[optionKey]
    const optionParam = Array.isArray(rawOptionValue) ? rawOptionValue[0] : rawOptionValue
    if (typeof optionParam === 'string' && optionParam) {
      option = getFullNameFromHyphenatedName(optionParam)
    }
    if (option) {
      const optionString = option
      const optionValue = product.productOptions
        .find(productOption => productOption.name == optionName)
        ?.values?.find(value => matchOptionValue(value.value, optionString))
      if (optionValue) {
        return new ProductVariantOption({
          name: optionName,
          value: optionValue.value,
        })
      }
    }
  }
  return undefined
}

export const hasOnlySizeOptionChanged = (
  originalParams: URLSearchParams,
  updatedParams: URLSearchParams,
  productOptionNames: string[],
): boolean => {
  let sizeChanged = false

  const originalSize = originalParams.get('size')
  const updatedSize = updatedParams.get('size')
  if (originalSize !== updatedSize) {
    sizeChanged = true
  }

  for (const [key, value] of updatedParams.entries()) {
    if (key !== 'size' && originalParams.get(key) !== value) {
      return false
    }
  }

  for (const productOptionName of productOptionNames) {
    const originalOption = originalParams.get(productOptionName)
    const updatedOption = updatedParams.get(productOptionName)
    if (originalOption !== updatedOption) {
      return false
    }
  }

  return sizeChanged
}

export const updateOptionsInUrl = (
  pathname: string,
  searchParams: ReadonlyURLSearchParams,
  selectedOptions: ProductVariantOption[],
  router: QuinceRouter,
  shouldUsePdpNavigationNew: boolean,
) => {
  // replaceWithHistoryApi bypasses the Next router, so the useSearchParams() snapshot goes stale after
  // the first write — diff against the live URL on that path, or reselecting the landing option is
  // treated as a no-op and the URL keeps the previous value
  const currentSearchParams =
    shouldUsePdpNavigationNew && isBrowser()
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams(Array.from(searchParams.entries())) // -> has to use this form
  const updatedSearchParams = new URLSearchParams(Array.from(currentSearchParams.entries()))

  const colorOption = selectedOptions.filter(o => o.name == ProductOptionName.Color)[0]?.value
  const sizeOption = selectedOptions.filter(o => o.name == ProductOptionName.Size)[0]?.value
  const productOptionNames = getProductOptionNamesFromSelectedOptions(selectedOptions)
  if (sizeOption) {
    updatedSearchParams.set('size', getURLStringFromInputString(sizeOption))
  }
  if (colorOption) {
    updatedSearchParams.set('color', getURLStringFromInputString(colorOption))
  }
  productOptionNames.forEach(name => {
    const option = selectedOptions.filter(o => o.name == name)[0]?.value
    if (option) {
      updatedSearchParams.set(name.toLowerCase(), getURLStringFromInputString(option))
    }
  })
  const routerOptions = hasOnlySizeOptionChanged(currentSearchParams, updatedSearchParams, productOptionNames)
    ? { shallow: true }
    : undefined
  updateSearchParams(
    currentSearchParams,
    updatedSearchParams,
    router,
    pathname,
    routerOptions,
    shouldUsePdpNavigationNew,
  )
}

export const updateSearchParams = (
  originalSearchParams: URLSearchParams,
  updatedSearchParams: URLSearchParams,
  router: QuinceRouter,
  pathname: string,
  routerOptions?: { shallow: boolean },
  useHistoryApi?: boolean,
) => {
  const newSearchString = updatedSearchParams.toString()
  const originalSearchString = originalSearchParams.toString()
  if (newSearchString !== originalSearchString) {
    const query = newSearchString ? `?${newSearchString}` : ''
    const newUrl = `${pathname}${query}`
    const windowHistoryState = getWindowHistoryState(newUrl)
    if (useHistoryApi) {
      router.replaceWithHistoryApi(newUrl, windowHistoryState)
    } else {
      router.replace(newUrl, newUrl, {
        scroll: false,
        shallow: false,
        ...routerOptions,
      })
    }
  }
}

const matchOptionValue = (value1: string, value2: string) => {
  return value1.toLowerCase().replace(/[^a-z0-9]*/g, '') === value2.toLowerCase().replace(/[^a-z0-9]*/g, '')
}

export const getWindowHistoryState = (url: string) => {
  const currentState = window.history.state
  return {
    ...currentState,
    as: url,
    url: url,
    options: {
      ...currentState?.options,
      shallow: false,
      scroll: false,
    },
  }
}
